import { createContext, useContext, useMemo } from 'react';
import { ofetch, FetchError } from 'ofetch';
import { useAuth } from '../auth/AuthProvider';
import { useBrandRef } from '../brand/BrandProvider';
import { ApiClientError } from './errors';

/**
 * Resolved API base URL.
 *  - Production: window.__ORBIT_CONFIG__.coreApiUrl (injected by server.js)
 *  - Dev: '' → Vite proxy auf /v1 (siehe vite.config.js)
 */
function resolveBaseUrl() {
  if (typeof window !== 'undefined' && window.__ORBIT_CONFIG__?.coreApiUrl) {
    return window.__ORBIT_CONFIG__.coreApiUrl;
  }
  return '';
}

/**
 * Fallback-Brand-Origin für Auth-Calls VOR /v1/operator/brands.
 *
 * Priorität:
 *  1. Runtime-Config (Prod: server.js injectet aus DEFAULT_BRAND_ORIGIN env)
 *  2. Dev-Env (VITE_DEFAULT_BRAND_ORIGIN aus .env.local)
 *  3. Dev-Build: Hardcoded 'valitype.com' als letzter Anker
 *  4. Prod-Build: NULL — Caller muss explizit Brand wählen vor Login
 *
 * Per security-review MEDIUM-1: kein implizierter Fallback in Prod, damit
 * Login nicht versehentlich gegen falsche Brand authentifiziert.
 */
function getDefaultBrandOrigin() {
  if (typeof window !== 'undefined' && window.__ORBIT_CONFIG__?.defaultBrandOrigin) {
    return window.__ORBIT_CONFIG__.defaultBrandOrigin;
  }
  if (import.meta.env.VITE_DEFAULT_BRAND_ORIGIN) {
    return import.meta.env.VITE_DEFAULT_BRAND_ORIGIN;
  }
  if (import.meta.env.PROD) return null;
  return 'valitype.com';
}

function buildClient(auth, brandRef) {
  const baseURL = resolveBaseUrl();
  let refreshInflight = null;

  /**
   * Web-Locks-API für Cross-Tab-Refresh-Synchronisation. Wenn zwei Tabs
   * gleichzeitig 401 bekommen, würde ohne Lock jedes Tab einen eigenen
   * Refresh feuern und die Token-Rotation des einen würde den anderen
   * invalidieren ("invalid_grant" → ungewollter Logout).
   *
   * Per security-review LOW-1. Innerhalb des Locks: Token-State neu lesen
   * (anderer Tab könnte schon geliefert haben) und nur dann tatsächlich
   * refreshen, wenn das Access-Token immer noch das alte ist.
   */
  const withRefreshLock = async (fn) => {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request('orbit_refresh', { mode: 'exclusive' }, fn);
    }
    return fn();
  };

  const performLegacyRefresh = async () => {
    const refreshToken = auth.getRefreshToken();
    if (!refreshToken) throw new Error('no_refresh_token');
    // Brand-Origin MUSS mit: /v1/auth/refresh liegt in Core hinter
    // `requireTenant` und antwortet ohne den Header mit 400.
    //
    // Dieser Aufruf geht bewusst am `raw`-Client vorbei — ginge er darüber,
    // würde dessen onResponseError bei einem 401 erneut einen Refresh
    // auslösen. Der Preis dafür: der onRequest-Hook läuft ebenfalls nicht,
    // und genau der setzt sonst Brand-Origin. Der Header muss hier also von
    // Hand gesetzt werden, mit derselben Auflösung wie im Hook.
    //
    // Ohne ihn scheiterte JEDE Anmeldung: Login lieferte 200, der direkt
    // folgende Refresh 400, und der catch in doRefresh() ruft
    // `auth.signOut('refresh_failed')` — die Oberfläche sprang kommentarlos
    // zum Login-Formular zurück. Gemessen an core: Login trug
    // `brand-origin: valitype.com`, der Refresh trug ihn nicht.
    const brand = brandRef.current ?? getDefaultBrandOrigin();
    const res = await ofetch('/v1/auth/refresh', {
      baseURL,
      method: 'POST',
      headers: brand ? { 'Brand-Origin': brand } : undefined,
      body: { refreshToken },
    });
    auth.setTokens(res);
  };

  const doRefresh = async (stalenessToken) => {
    if (refreshInflight) return refreshInflight;

    refreshInflight = (async () => {
      try {
        await withRefreshLock(async () => {
          // Innerhalb des Locks prüfen ob ein anderer Tab schon refresht hat.
          // tokenStorage wird beim setTokens() jedes Tabs aktualisiert; wenn
          // das Token-Trigger anders ist als beim Lock-Start, sind wir durch.
          const current = auth.getAccessToken();
          if (current && current !== stalenessToken) return;
          await performLegacyRefresh();
        });
      } catch (err) {
        auth.signOut('refresh_failed');
        throw err;
      } finally {
        refreshInflight = null;
      }
    })();
    return refreshInflight;
  };

  const raw = ofetch.create({
    baseURL,
    onRequest({ options, request }) {
      const headers = new Headers(options.headers);
      const token = auth.getAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);

      const url = typeof request === 'string' ? request : request.url;
      // Per Spec (operator-bootstrap.md): NUR /v1/operator/brands ist brandless.
      // /v1/auth/login + /refresh + /mfa/verify brauchen Brand-Origin
      // ("irgendeine erlaubte Brand") — Core validiert dass die Brand existiert
      // und der User dort Membership hat.
      //
      // Caller kann Brand-Origin explizit overriden (z.B. für brand-übergreifende
      // Admin-Stats die über alle Brands iterieren). Wir respektieren explizite
      // Header und überschreiben sie NICHT.
      const isBrandless = url.includes('/v1/operator/brands');
      const hasExplicit = headers.has('Brand-Origin');
      if (!hasExplicit) {
        const brand = brandRef.current ?? getDefaultBrandOrigin();
        if (!isBrandless && brand) headers.set('Brand-Origin', brand);
      }

      options.headers = headers;
    },
    async onResponseError({ response, request, options }) {
      if (response?.status !== 401) return;
      const url = typeof request === 'string' ? request : request.url;
      if (url.includes('/v1/auth/refresh')) return;

      const headers = new Headers(options.headers);
      if (headers.get('X-Orbit-Retry') === '1') return;

      // Token aus Header lesen für Staleness-Vergleich im Lock.
      const staleToken = headers.get('Authorization')?.slice(7) ?? null;
      await doRefresh(staleToken);
      // Nach Refresh: aktuelles Access-Token verwenden (anderer Tab könnte
      // schon refresht haben). Brand-Origin und sonstige Headers behalten.
      const fresh = auth.getAccessToken();
      if (fresh) headers.set('Authorization', `Bearer ${fresh}`);
      headers.set('X-Orbit-Retry', '1');
      const retried = await ofetch.raw(request, { ...options, headers });
      Object.assign(response, retried);
    },
  });

  // Wrap FetchError → ApiClientError mit stabilem `code`
  return async function client(req, opts) {
    try {
      return await raw(req, opts);
    } catch (err) {
      if (err instanceof FetchError) {
        const data = err.data;
        const code = data?.error?.code ?? 'unknown_error';
        const message = data?.error?.message ?? err.message;
        throw new ApiClientError({
          code,
          message,
          status: err.response?.status ?? 0,
          details: data?.error?.details,
        });
      }
      throw err;
    }
  };
}

const ApiClientContext = createContext(null);

export function ApiClientProvider({ children }) {
  const auth = useAuth();
  const brandRef = useBrandRef();

  const value = useMemo(() => {
    // brandRef wird in `onRequest` (Aufruf-Zeit) dereferenziert,
    // nicht während Render — kein Lint-Konflikt mit react-compiler-strict.
    const fetch = buildClient(
      {
        getAccessToken: auth.getAccessToken,
        getRefreshToken: auth.getRefreshToken,
        setTokens: auth.setTokens,
        signOut: auth.signOut,
      },
      brandRef,
    );

    return {
      fetch,
      login: (email, password) =>
        fetch('/v1/auth/login', {
          method: 'POST',
          body: { email, password },
        }),
      verifyMfa: (mfaChallenge, code) =>
        fetch('/v1/auth/mfa/verify', {
          method: 'POST',
          body: { mfaChallenge, code },
        }),
    };
  }, [auth, brandRef]);

  return <ApiClientContext.Provider value={value}>{children}</ApiClientContext.Provider>;
}

export function useApiClient() {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error('useApiClient must be used inside <ApiClientProvider>');
  return ctx;
}
